import {
  AssetMarkedByIndicator,
  DataQualityDetailProps,
  VizPortalDQWDataCatalogProvider,
  VizPortalDQWDataCatalogProviderProps,
  WarningStatusChip,
  WarningStatusProps
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import { IDataQualityIndicator } from '@tableau/vizportal-webclient-api/lib/api/types/IDataQualityIndicator';
import * as React from 'react';
import { Query } from 'react-apollo';

import {
  VirtualConnectionLineageUpstreamDataQualityWarningsQuery,
  VirtualConnectionLineageUpstreamDataQualityWarningsQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionLineageUpstreamDataQualityWarningsQuery';
import { VirtualConnectionLineageUpstreamDataQualityWarningsQueryGql } from '../../queries/VirtualConnectionLineageUpstreamDataQualityWarningsQueryGql';
import { ErrorListProvider } from '../error/components/ErrorListProvider';
import { renderQueryResult } from '../renderQueryResult';
import { emptyWarnings, filterOutWarningsFromAnchorUntilCycleIsFixed } from './DataQualityWarningCycleTemporaryFix';

interface LineageWarningVirtualConnectionProps {
  readonly asset: AssetMarkedByIndicator;
  readonly vizportalUrlId: string;
  readonly ownContentWarnings: ReadonlyArray<DataQualityDetailProps>;
  readonly anchoredWarnings: ReadonlyArray<IDataQualityIndicator>;
}

export type EmbeddableVirtualConnectionDataQualityWarningProps = VizPortalDQWDataCatalogProviderProps & LineageWarningVirtualConnectionProps;

export function EmbeddableVirtualConnectionDataQualityWarning(props: EmbeddableVirtualConnectionDataQualityWarningProps): JSX.Element {
  const { featureFlags: { publishedConnectionsLineageEnabled }, siteUrls: urls } = props;

  return (
    <VizPortalDQWDataCatalogProvider {...props}>
      <ErrorListProvider>
        <Query<VirtualConnectionLineageUpstreamDataQualityWarningsQuery, VirtualConnectionLineageUpstreamDataQualityWarningsQueryVariables>
          query={VirtualConnectionLineageUpstreamDataQualityWarningsQueryGql}
          variables={{ vizportalUrlId: props.vizportalUrlId, first: 10, offset: 0, publishedConnectionsLineageEnabled }}
        >
          {result => renderQueryResult(result, {
            renderLoading: () => renderWarnings(urls, { ...props }),
            renderData: data => renderWarnings(urls, { ...props }, data),
          })}
        </Query>
      </ErrorListProvider>
    </VizPortalDQWDataCatalogProvider>
  );
}

function renderWarnings(
  urls: SiteUrls,
  args: Pick<EmbeddableVirtualConnectionDataQualityWarningProps, 'asset' | 'anchoredWarnings'>,
  data?: VirtualConnectionLineageUpstreamDataQualityWarningsQuery
): JSX.Element | null {
  const virtualConnection = data && data.virtualConnections ? data.virtualConnections[0] : null;
  const anchoredWarnings = args.anchoredWarnings.filter(w => w.isActive)
  if (!virtualConnection && anchoredWarnings.length < 1) {
    return null;
  }

  const upstreamWarnings = virtualConnection
    ? filterOutWarningsFromAnchorUntilCycleIsFixed(virtualConnection.id, virtualConnection.upstreamDataQualityWarningsConnection?.nodes ?? emptyWarnings)
    : emptyWarnings

  const props: WarningStatusProps = {
    subjectArea: 'VirtualConnection',
    kind: 'vizportal',
    anchoredAsset: args.asset,
    iconOnly: false,
    anchoredWarnings,
    upstreamWarnings,
    urls,
  }

  return <WarningStatusChip {...props} />
}
